<template>
  <Modal
    ref="modal"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ tradeItem ? $t('HeadContract.BillOfQuantities.TradeItemSaveModal.update') : $t('HeadContract.BillOfQuantities.TradeItemSaveModal.create') }} {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full overflow-y-auto">
        <form
          action=""
          class="flex flex-col space-y-4"
          @submit.prevent="saveTrade"
        >
          <div>
            <p class="text-lg font-semibold">
              {{ tradeItem ? $t('HeadContract.BillOfQuantities.TradeItemSaveModal.under') : $t('HeadContract.BillOfQuantities.TradeItemSaveModal.addingUnder') }} {{ trade.name }}
            </p>

            <p
              v-if="parentTradeItem"
              class="text-gray-500"
            >
              {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.addingUnder') }} {{ parentTradeItem.name }}
            </p>
          </div>

          <p
            v-if="tradeItemForm.hasErrors('parent_id')"
            class="text-red-800 text-sm bg-red-100 p-2 rounded-md"
          >
            {{ tradeItemForm.getErrors('parent_id') }}
          </p>

          <p
            v-if="parentTradeItem && !parentTradeItem.has_children"
            class="text-yellow-800 bg-yellow-100 p-2 rounded-md"
          >
            Adding this item under "{{ parentTradeItem.name }}" will convert "{{ parentTradeItem.name }}" to a parent item.
          </p>

          <label
            v-if="!parentTradeItem && availableParentTradeItems.length > 0 && !props.tradeItem"
          >
            {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.label.parentTradeItem') }}
            <AppSelect
              v-model.number="tradeItemForm.form.parent_id"
              name="parent_id"
              class="form-input"
              :options="availableParentTradeItems"
              :error="tradeItemForm.getErrors('parent_id')"
              :optional="true"
              :empty-text="$t('HeadContract.BillOfQuantities.TradeItemSaveModal.selectParent')"
              data-test="parent-trade-item-select"
            />
          </label>

          <div v-if="!tradeItem">
            <label>
              Create as parent item
              <input
                v-model="tradeItemForm.form.is_parent"
                name="is_parent"
                type="checkbox"
                class="form-checkbox"
              >
            </label>
          </div>

          <label>
            {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.label.description') }}
            <AppTextArea
              v-model="tradeItemForm.form.name"
              name="name"
              type="text"
              class="form-input resize-none"
              :error="tradeItemForm.getErrors('name')"
              data-test="name-input"
              rows="5"
            />
          </label>

          <template v-if="!tradeItemForm.form.is_parent && !tradeItem?.has_children">
            <div class="flex space-x-4 w-full">
              <label class="w-full">
                {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.label.unit') }}

                <UnitsTypeAhead
                  v-model="tradeItemForm.form.unit"
                  name="unit"
                  :error="tradeItemForm.getErrors('unit')"
                />
              </label>

              <label class="w-full">
                {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.label.quantity') }}
                <AppInput
                  v-model="tradeItemForm.form.quantity"
                  class="form-input"
                  name="quantity"
                  type="number"
                  :error="tradeItemForm.getErrors('quantity')"
                  data-test="quantity-input"
                />
              </label>
            </div>

            <label>
              {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.label.rate') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }}/{{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.hint.unit') }})</span>

              <AppCurrencyInput
                v-model="tradeItemForm.form.rate"
                class="form-input"
                name="rate"
                :error="tradeItemForm.getErrors('rate')"
                data-test="rate-input"
              />
            </label>

            <label>
              {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.label.budget') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }})</span>

              <CurrencyInput
                v-model="tradeItemForm.form.budget"
                class="form-input disabled:bg-gray-300"
                name="budget"
                :error="tradeItemForm.getErrors('budget')"
                data-test="budget-input"
                disabled
              />
            </label>

            <label>
              Provisional sum
              <input
                v-model="tradeItemForm.form.is_provisional_sum"
                name="is_provisional_sum"
                type="checkbox"
                class="form-checkbox"
                data-test="is-provisional-sum-input"
              >
            </label>
          </template>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end p-2 space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="modal.close(); tradeItemForm.resetData(); "
        >
          {{ $t('HeadContract.BillOfQuantities.TradeItemSaveModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="createTradeItemAction.is(States.LOADING)"
          data-test="save-trade-item"
          @click="saveTrade"
        >
          {{ tradeItem ? $t('HeadContract.BillOfQuantities.TradeItemSaveModal.update') : $t('HeadContract.BillOfQuantities.TradeItemSaveModal.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import CurrencyInput from '@/components/app/CurrencyInput.vue';
import Modal from '@/components/Modal.vue';
import UnitsTypeAhead from '@/components/UnitsTypeAhead.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, useTemplateRef, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  trade: Trade;
  tradeItem?: TradeItem;
  selectedPosition?: number;
  parentTradeItem?: TradeItem;
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'updateSort', data: { tradeItem: TradeItem; index: number });
}>();

const modal = useTemplateRef('modal');

const tradeItemForm = useFormData({
  name: '',
  budget: 0,
  trade_id: props.trade.id,
  parent_id: props.parentTradeItem?.id,
  is_parent: undefined,
  rate: 0,
  quantity: 0,
  unit: '',
  sort_order: undefined,
  test_currency: 0,
  is_provisional_sum: false,
});

watch(() => props.selectedPosition, (newValue) => {
  tradeItemForm.form.sort_order = newValue;
}, { immediate: true });

watch(() => [tradeItemForm.form.rate, tradeItemForm.form.quantity], (newValue) => {
  if(newValue[0] !== 0 && newValue[1] > 0) {
    tradeItemForm.form.budget = newValue[0] * newValue[1];
  }
});

watch(() => props.tradeItem, (newValue) => {
  if(newValue) {
    tradeItemForm.setData(newValue);
  }
}, { immediate: true });

const tradeItemsStore = useTradeItemsStore();
const createTradeItemAction = useStoreApiAction(tradeItemsStore.createTradeItem);
const updateTradeItemAction = useStoreApiAction(tradeItemsStore.updateTradeItem);

// Get available parent trade items (only items that already have children)
const availableParentTradeItems = computed(() => {
  const parentItems = tradeItemsStore.models
    .where('trade_id', props.trade.id)
    .where('has_children', true)
    .get();

  const allItems = tradeItemsStore.models
    .where('trade_id', props.trade.id)
    .get();

  return parentItems.map((item) => ({
    value: item.id,
    label: `${item.fullBillItem(allItems)} ${item.name}`,
  }));
});

const saveTrade = () => {
  tradeItemForm.resetErrors();

  if(props.tradeItem) {
    updateTradeItemAction.request(props.tradeItem.id, tradeItemForm.form).then(() => {
      toast.success('Trade item updated.');
      emits('close');
    }).catch((error) => {
      tradeItemForm.setErrors(error.data);
    });
  } else {
    createTradeItemAction.request(props.trade.project_id, tradeItemForm.form).then((data) => {
      emits('updateSort', { tradeItem: data, index: data.sort_order });

      toast.success('Trade item created.');
      emits('close');
    }).catch((error) => {
      tradeItemForm.setErrors(error.data);
    });
  }
};
</script>

<style scoped></style>
